import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import Inicio from './Inicio';
import MenuMoviles from './MenuMoviles';
import Products from './Products';
import NewProduct from './NewProduct';
import EditProduct from './EditProduct';
import Clientes from './Clientes';
import ClientePagina from './ClientePagina';
import Orders from './Orders';
import Messages from './Messages';
import Statistics from './Statistics';
import Reports from './Reports';
import MarketAnalysis from './MarketAnalysis';
import Cotizador from './Cotizador';
import Etiquetas from './Etiquetas';
import Recibos from './Recibos';
import AdminStock from './AdminStock';
import AdminWarehouseStock from './AdminWarehouseStock';
import PriceIncreases from './PriceIncreases';
import Proveedores from './Proveedores';
import CuentasCorrientes from './CuentasCorrientes';
import CuentaCorrienteDetalle from './CuentaCorrienteDetalle';
import ProveedorPagina from './ProveedorPagina';
import AdminCheques from './AdminCheques';
import AdminBusinessStats from './AdminBusinessStats';
import MonthlyOrders from './MonthlyOrders';
import Settings from './Settings';
import Security from './Security';
import { useAuth } from '../../contexts/AuthContext';

const Admin: React.FC = () => {
  const { user, isLoading } = useAuth();

  // En un refresh la sesion se restaura de forma asincrona. No ejecutar los
  // redirects hasta que /auth/verify haya terminado.
  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-100 text-slate-600">Verificando sesión…</div>;
  }

  // El backend ya rechaza cada endpoint admin sin el rol correcto, pero no
  // hay que depender solo de eso: si no hay sesion o el rol no es admin,
  // ni siquiera se renderiza el shell del panel.
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!['admin', 'administrador', 'superadmin'].includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  // Un admin restringido a ciertos clientes (allowedClientIds) solo debe ver
  // las secciones relacionadas a esos clientes: nada de Dashboard, catalogo,
  // pedidos, etc. Cualquier otra ruta redirige a su punto de entrada.
  const allowedClientIds = Array.isArray(user.allowedClientIds) ? user.allowedClientIds : null;
  const restricted = allowedClientIds !== null;
  const restrictedHome = restricted
    ? (allowedClientIds!.length === 1 ? `/admin/clientes/${allowedClientIds![0]}` : '/admin/clientes')
    : '/admin';
  const gate = (element: React.ReactElement) => (restricted ? <Navigate to={restrictedHome} replace /> : element);

  return (
    <AdminLayout>
      <Routes>
        <Route path="/" element={gate(<Inicio />)} />
        <Route path="/moviles" element={<MenuMoviles />} />
        <Route path="/productos" element={gate(<Products />)} />
        <Route path="/productos/nuevo" element={gate(<NewProduct />)} />
        <Route path="/productos/:id/editar" element={gate(<EditProduct />)} />
        <Route path="/usuarios" element={<Navigate to="/admin/configuracion" replace />} />
        <Route path="/clientes" element={<Clientes />} />
        <Route path="/clientes/:clientId" element={<ClientePagina />} />
        <Route path="/pedidos" element={gate(<Orders />)} />
        <Route path="/pedidos-mensuales" element={<MonthlyOrders />} />
        <Route path="/mensajes" element={gate(<Messages />)} />
        <Route path="/estadisticas" element={gate(<Statistics />)} />
        <Route path="/reportes" element={gate(<Reports />)} />
        <Route path="/informes/stock" element={gate(<Reports />)} />
        <Route path="/informes/aumentos" element={gate(<PriceIncreases />)} />
        <Route path="/analisis-mercado" element={gate(<MarketAnalysis />)} />
        <Route path="/cotizador" element={gate(<Cotizador />)} />
        <Route path="/etiquetas" element={<Etiquetas />} />
        <Route path="/remitos" element={<Recibos />} />
        <Route path="/recibos" element={<Navigate to="/admin/remitos" replace />} />
        <Route path="/stock-clientes" element={gate(<AdminStock />)} />
        <Route path="/proveedores" element={<Proveedores />} />
        <Route path="/proveedores/:supplierId" element={<ProveedorPagina />} />
        <Route path="/stock-propio" element={<AdminWarehouseStock />} />
        <Route path="/cuentas-corrientes" element={<CuentasCorrientes />} />
        <Route path="/cuentas-corrientes/supplier/:supplierId" element={<ProveedorPagina backTo="/admin/cuentas-corrientes" backLabel="Volver a cuentas corrientes" />} />
        <Route path="/cuentas-corrientes/:partyType/:partyId" element={<CuentaCorrienteDetalle />} />
        <Route path="/cheques" element={<AdminCheques />} />
        <Route path="/estadisticas-negocio" element={<AdminBusinessStats />} />
        <Route path="/configuracion" element={gate(<Settings />)} />
        <Route path="/seguridad" element={user.role === 'superadmin' ? <Navigate to="/admin/seguridad/resumen" replace /> : <Navigate to="/admin" replace />} />
        <Route path="/seguridad/resumen" element={user.role === 'superadmin' ? <Security section="overview" /> : <Navigate to="/admin" replace />} />
        <Route path="/seguridad/analitica" element={user.role === 'superadmin' ? <Security section="analytics" /> : <Navigate to="/admin" replace />} />
        <Route path="/seguridad/backups" element={user.role === 'superadmin' ? <Security section="backups" /> : <Navigate to="/admin" replace />} />
        <Route path="/seguridad/eventos" element={user.role === 'superadmin' ? <Security section="events" /> : <Navigate to="/admin" replace />} />
        <Route path="/seguridad/actividad" element={user.role === 'superadmin' ? <Security section="activity" /> : <Navigate to="/admin" replace />} />
        <Route path="*" element={<Navigate to={restrictedHome} replace />} />
      </Routes>
    </AdminLayout>
  );
};

export default Admin;
